import Box from '@mui/material/Box'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useState, useEffect, memo } from 'react'
import { makeStyles } from 'tss-react/mui'

import cloudinary from '@src/lib/Cloudinary'
import { campaignMinimumSpend } from '@src/lib/Utils'

import breakpoints from '../../../lib/Breakpoints'

import CampaignCardBody from './CampaignCardBody'
import CampaignCardCTA from './CampaignCardCTA'
import CampaignInfo from './CampaignInfo'
const useStyles = makeStyles()((theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(8),
    overflow: 'hidden',
  },
  root: {
    [`@media (min-width: ${breakpoints.tablet})`]: {
      //Really want to use theme.breakpoints here but want to not introduce inconsistencies in the design right now
      'width': 'calc(50% - 24px)',
      '&:nth-of-type(odd)': {
        /*Leftmost column*/
        marginRight: '48px',
      },
    },
    [`@media (min-width: ${breakpoints.desktop})`]: {
      'width': 'calc(33% - 20px)',
      'marginRight': 0,
      '&:nth-of-type(odd)': {
        marginRight: 0,
      },
      '&:nth-of-type(3n + 1)': {
        /*Leftmost column*/
        marginRight: '35px',
      },
      '&:nth-of-type(3n + 3)': {
        /*Rightmost column*/
        marginLeft: '35px',
      },
    },
  },
  clickableBox: {
    cursor: 'pointer',
  },
}))

const CampaignCardTransition = ({ index, children }) => {
  const [animateIn, setAnimateIn] = useState(index === 0 ? true : false)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setAnimateIn(true)
    }, index * 100)
    return () => {
      clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (index !== 0) {
    return <Grow in={animateIn}>{children}</Grow>
  } else {
    return children
  }
}

const CampaignCard = (props) => {
  const { classes } = useStyles()
  return (
    <CampaignCardTransition index={props.index}>
      <Paper
        className={clsx(classes.wrapper, {
          [classes.root]: !props.dropLegacyStyles,
        })}
        elevation={5}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          onClick={props.onViewOrActivate}
          className={classes.clickableBox}
        >
          <CampaignCardBody
            brandUuid={props.brandUuid}
            brandLogo={props.brandLogo}
            description={props.description}
            endDate={props.endDate}
            startDate={props.startDate}
            stats={props.statsByCampaignId[props.id]}
            thumbnailSrc={cloudinary.instance().url(props.cardImage, {
              background: 'auto:predominant',
              crop: 'pad',
              width: 500 * 2,
              height: 180 * 2,
              fetch_format: 'jpg',
              quality: 'auto',
            })}
            title={props.title}
            type={props.type}
            paidType={props.paidType}
            sash={props.sash}
            creditAmount={props.creditAmount}
          />
        </Box>
        <Box display={'flex'} flexDirection={'column'}>
          <CampaignInfo
            channelActivated={props.channelActivated}
            channelOptions={props.channelOptions}
            channels={props.channels}
            creditAmount={
              props.paidType === 'retailer_funded'
                ? campaignMinimumSpend(props.startDate, props.endDate) * 100 // component expects cents
                : props.creditAmount
            }
            isActivated={props.isActivated}
            startDate={props.startDate}
            id={props.id}
            isFeatured={props.isFeatured}
            platforms={props.platforms}
            type={props.type}
            paidType={props.paidType}
            viewOrActivate={props.onViewOrActivate}
          />
          <CampaignCardCTA
            isNew={props.sash}
            id={props.id}
            isActivated={props.isActivated}
            startDate={props.startDate}
            type={props.type}
            paidType={props.paidType}
            viewOrActivate={props.onViewOrActivate}
          />
        </Box>
      </Paper>
    </CampaignCardTransition>
  )
}

CampaignCard.propTypes = {
  addFunds: PropTypes.func,
  brandLogo: PropTypes.string,
  brandName: PropTypes.string,
  channelActivated: PropTypes.shape({
    facebook: PropTypes.bool,
    instagram: PropTypes.bool,
    email: PropTypes.bool,
    embed: PropTypes.bool,
    twitter: PropTypes.bool,
    assets: PropTypes.bool,
  }),
  channelOptions: PropTypes.shape({
    facebook: PropTypes.number,
    instagram: PropTypes.number,
    email: PropTypes.number,
    embed: PropTypes.number,
    twitter: PropTypes.number,
    assets: PropTypes.number,
  }),
  channels: PropTypes.array,
  creditAmount: PropTypes.number,
  currentSlugs: PropTypes.shape({
    retailerAccount: PropTypes.string,
    brand: PropTypes.string,
  }),
  description: PropTypes.string,
  dismiss: PropTypes.func,
  endDate: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  index: PropTypes.number,
  isActivated: PropTypes.bool,
  isFeatured: PropTypes.bool,
  onViewOrActivate: PropTypes.func,
  platforms: PropTypes.array,
  sash: PropTypes.bool,
  slug: PropTypes.string,
  startDate: PropTypes.string,
  statsByCampaignId: PropTypes.object,
  thumbnailSrc: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.oneOf(['paidAd', 'localAd', 'campaign', 'contentDiscovery']),
  dropLegacyStyles: PropTypes.bool,
}

CampaignCard.defaultProps = {
  addFunds: () => alert('Add Funds'),
  dismiss: () => alert('Dismiss'),
  index: 0,
}

export default memo(CampaignCard)
